import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, ListMusic, MicVocal, Music, Play, X } from "lucide-react";
import { getEpisodeLyrics } from "@/data/episodeLyrics";
import { trackEvent } from "@/utils/track";

let ytApiPromise;
const loadYouTubeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (previous) previous();
        resolve();
      };
    });
  }
  return ytApiPromise;
};

const getEmbedUrl = (youtubeId) => `https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1&autoplay=1&enablejsapi=1`;

export function MediaPlayerModal({ kind, items, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [embedError, setEmbedError] = useState(false);
  const frameRef = useRef(null);
  const playerRef = useRef(null);
  const lyricsRef = useRef(null);
  const item = items[index];
  const isSong = kind === "song";
  const hasQueue = items.length > 1;
  const lyrics = isSong ? getEpisodeLyrics(item.episodeNumber) : [];
  const watchUrl = `https://youtu.be/${item.youtubeId}`;
  const itemLabel = item.label || (isSong ? `Episode ${item.episodeNumber} Song` : `Episode ${item.episodeNumber}`);

  useEffect(() => setEmbedError(false), [index]);

  useEffect(() => {
    trackEvent("play", { media: item.mediaKey || `${kind}-${item.episodeNumber}` });
  }, [index, kind, item.episodeNumber, item.mediaKey]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then(() => {
      if (cancelled || !frameRef.current) return;
      playerRef.current = new window.YT.Player(frameRef.current, {
        events: {
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.ENDED && hasQueue) {
              setIndex((current) => (current + 1) % items.length);
            }
          },
          onError: (event) => {
            if ([100, 101, 150].includes(event.data)) setEmbedError(true);
          },
        },
      });
    });
    return () => {
      cancelled = true;
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          // Player iframe already removed by React; safe to ignore.
        }
        playerRef.current = null;
      }
    };
  }, [index, hasQueue, items.length]);

  useEffect(() => {
    const node = lyricsRef.current;
    if (!node || lyrics.length === 0) return undefined;
    node.scrollTop = 0;
    const timer = window.setInterval(() => {
      if (node.matches(":hover")) return;
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 2) node.scrollTop = 0;
      else node.scrollTop += 1;
    }, 90);
    return () => window.clearInterval(timer);
  }, [index, lyrics.length]);

  return (
    <div className="ns-song-modal" role="dialog" aria-modal="true" aria-label={isSong ? "Song player" : "Episode player"} data-testid="season-media-modal" onClick={onClose}>
      <div className="ns-song-modal-card" onClick={(event) => event.stopPropagation()}>
        <div className="ns-song-modal-head">
          <p>{isSong ? <Music size={14} /> : <Play size={14} />} {itemLabel} · {item.title}</p>
          <div className="ns-song-modal-actions">
            {hasQueue && <span className="ns-song-modal-count" data-testid="playlist-position">{index + 1} / {items.length}</span>}
            <a className="ns-song-modal-ext" href={watchUrl} target="_blank" rel="noreferrer" data-testid="media-open-youtube" aria-label="Open on YouTube"><ExternalLink size={15} /></a>
            <button type="button" data-testid="season-media-modal-close" aria-label="Close player" onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        <div className="ns-song-modal-frame">
          {embedError ? (
            <div className="ns-song-modal-fallback" data-testid="media-embed-fallback">
              <Music size={26} />
              <p>This video cannot play inside the website yet.</p>
              <a href={watchUrl} target="_blank" rel="noreferrer" data-testid="media-fallback-link"><Play size={14} fill="currentColor" /> Watch on YouTube</a>
            </div>
          ) : (
            <iframe key={item.youtubeId} ref={frameRef} src={getEmbedUrl(item.youtubeId)} title={isSong ? `Episode ${item.episodeNumber} song` : `Episode ${item.episodeNumber}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          )}
        </div>
        {hasQueue && (
          <div className="ns-song-modal-queue">
            <button type="button" data-testid="playlist-prev" disabled={index === 0} onClick={() => setIndex((current) => Math.max(0, current - 1))}><ChevronLeft size={15} /> Prev</button>
            <span><ListMusic size={13} /> Season 1 sing-along playlist</span>
            <button type="button" data-testid="playlist-next" disabled={index === items.length - 1} onClick={() => setIndex((current) => Math.min(items.length - 1, current + 1))}>Next <ChevronRight size={15} /></button>
          </div>
        )}
        {isSong && (
          <div className="ns-song-lyrics" data-testid="song-lyrics" ref={lyricsRef}>
            <p className="ns-song-lyrics-title"><MicVocal size={13} /> Sing along</p>
            {lyrics.length > 0 ? lyrics.map((line, lineIndex) => <span key={lineIndex}>{line}</span>) : <span className="is-empty">Lyrics coming soon</span>}
          </div>
        )}
      </div>
    </div>
  );
}
